import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageUploadArea } from '../../components/ImageUploadArea';
import { ImageGrid, ImageGridImage } from '../../components/ImageGrid';
import { SectionHead } from '../../components/edit-object/SectionHead';
import { AddMoreTile } from '../../components/edit-object/AddMoreTile';
import { EDIT_OBJECT_TOKENS } from '../../components/edit-object/editObjectTokens';
import { MAX_IMAGES_PER_ITEM } from '../../utils/uploadConstants';
import { deriveFilenameTitle } from '../../utils/uploadUtils';
import { useUpload } from '../../hooks/useUpload';
import { useUploadCoverImage } from '../../hooks/useUploadCoverImage';
import { useUploadImageOrder } from '../../hooks/useUploadImageOrder';
import { useDeleteImage } from '../../hooks/useDeleteImage';
import { UploadQueue } from '../../components/UploadQueue';
import { useMultipartUpload } from '../../hooks/useMultipartUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { logger } from '../../utils/logger';
import { selectPreviewUrl } from '../../utils/uploadPreview';
import { en } from '../../translations/en';

export const AUTO_CLEAR_DELAY_MS = 3000;

interface ValidationError {
  file: File;
  error: string;
}

interface UploadTabProps {
  ownerId?: string | null;
  /**
   * LL-NXX of the catalog the new item should be minted under. Pre-PR-4c,
   * the backend defaulted to the user's primary catalog — but when the
   * user has multiple owned catalogs and has selected a non-primary one,
   * we forward the selection so the catalog number is sequenced under
   * the active catalog instead.
   */
  catalogAliId?: string | null;
  /** Per-image visibility toggle (lifted from useUploadImageVisibility) */
  toggleVisibility?: (imageId: string) => Promise<void>;
  /** Error from image visibility operations */
  imageVisibilityError?: string | null;
  /**
   * When true, renders the design-mock flat-flow layout (dropzone → info
   * banner → section header → unified grid) instead of the legacy two-
   * accordion layout. Set by the new edit-object shell; the legacy
   * UploadObject path leaves this off so its accordion chrome stays put.
   */
  flat?: boolean;
}

export const UploadTab = ({ ownerId, catalogAliId, toggleVisibility, imageVisibilityError, flat = false }: UploadTabProps = {}) => {
  const { catalogId, uploadedImages, addImage, updateImageStatus, updateImageMetadata, selectedCoverId, setSelectedCoverId, setCatalogId, aliCatalogNumber, setAliCatalogNumber, setSuggestedTitle } = useUpload();
  const { markStepComplete } = useWizardCompletion();
  const visibilityError = imageVisibilityError ?? null;
  const { selectCoverImage, error: coverError } = useUploadCoverImage(ownerId);
  const { reorderAndPersist, error: reorderError } = useUploadImageOrder(ownerId);

  const { deleteImage, error: deleteError } = useDeleteImage(ownerId);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadStartError, setUploadStartError] = useState<string | null>(null);
  const [queueCollapsed, setQueueCollapsed] = useState(false);
  const { uploadFiles, uploadQueue, cancelUpload, retryUpload, clearCompleted, removeItem } = useMultipartUpload(ownerId, catalogAliId);

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setUploadStartError(null);

    // Regression test for #1225: derive a title from the primary image filename
    // the very first time images are added to a brand-new object.
    // Guard: uploadedImages is empty (no prior uploads this session) AND catalogId
    // is null (no catalog created yet = this is a new object, not edit mode).
    if (uploadedImages.length === 0 && !catalogId) {
      const derived = deriveFilenameTitle(files[0].name);
      if (derived) {
        setSuggestedTitle(derived);
      }
    }

    try {
      // Build file items with pre-assigned imageIds so upload queue and context share the same id.
      // We capture each file's filename + byte size up front so the image card
      // can show metadata before dimensions arrive (dimensions are read async via
      // an Image() probe so we don't block the upload start).
      const fileItems = files.map(file => {
        const imageId = crypto.randomUUID();
        addImage({
          imageId,
          file,
          status: 'pending',
          filename: file.name,
          fileSize: file.size,
        });

        // Probe natural dimensions in the background; resolve via updateImageMetadata
        // once known so the card metadata caption fills in.
        try {
          const probeUrl = URL.createObjectURL(file);
          const probe = new Image();
          probe.onload = () => {
            updateImageMetadata(imageId, {
              width: probe.naturalWidth,
              height: probe.naturalHeight,
            });
            URL.revokeObjectURL(probeUrl);
          };
          probe.onerror = () => {
            URL.revokeObjectURL(probeUrl);
          };
          probe.src = probeUrl;
        } catch (probeError) {
          // Browsers without URL.createObjectURL (or HEIC files some browsers
          // can't decode) just won't show dimensions — that's fine.
          logger.debug('UploadTab - dimension probe failed', probeError);
        }

        return { file, imageId };
      });
      uploadFiles(fileItems, catalogId);
      setValidationErrors([]);
    } catch (error) {
      logger.error('UploadTab - Failed to handle file upload', error);
      // Surface to UI: a thrown error before uploadFiles() means none of the
      // selected files made it into the queue. The user must know the batch
      // didn't start so they can retry.
      const message = error instanceof Error ? error.message : 'Unknown error';
      setUploadStartError(`Failed to start upload: ${message}`);
    }
  }, [uploadFiles, addImage, catalogId, updateImageMetadata, uploadedImages, setSuggestedTitle]);

  const handleValidationError = useCallback((errors: ValidationError[]) => {
    setValidationErrors(errors);
  }, []);

  const handleCoverSelect = useCallback((index: number) => {
    if (uploadedImages[index]) {
      selectCoverImage(uploadedImages[index].imageId);
    }
  }, [uploadedImages, selectCoverImage]);

  // After a drag-to-reorder, the leftmost image (slot 0) is the primary.
  // Persist the reorder first so the new order is the source of truth, then
  // promote the new slot-0 image to cover when it differs from the current
  // selection. The cover-selection API tolerates re-promotion of the same
  // imageId, so a no-op call is harmless if the user simply rearranged
  // tail-end images.
  const handleReorderAndPromote = useCallback(
    async (orderedImageIds: string[]) => {
      await reorderAndPersist(orderedImageIds);
      const newPrimaryId = orderedImageIds[0];
      if (newPrimaryId && newPrimaryId !== selectedCoverId) {
        await selectCoverImage(newPrimaryId);
      }
    },
    [reorderAndPersist, selectedCoverId, selectCoverImage],
  );

  // Auto-select first image as cover when no cover is selected
  useEffect(() => {
    if (!selectedCoverId && uploadedImages.length > 0 && catalogId) {
      const firstImage = uploadedImages[0];
      if (firstImage?.imageId) {
        selectCoverImage(firstImage.imageId);
      }
    }
  }, [selectedCoverId, uploadedImages, catalogId, selectCoverImage]);

  // Get selected cover index from context
  const selectedCover = selectedCoverId ? uploadedImages.findIndex(img => img.imageId === selectedCoverId) : null;

  // Cache for blob URLs to prevent recreation on every render
  const blobUrlCache = useRef<Map<string, string>>(new Map());

  // Memoize completed uploads to prevent new array reference on every render
  const completedUploads = useMemo(() =>
    uploadQueue.filter(item => item.status === 'completed' || item.status === 'processing'),
    [uploadQueue]
  );

  // SVG placeholder for images that haven't loaded yet
  const placeholderUrl = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="#f0f0f0" width="100" height="100"/><text x="50" y="50" text-anchor="middle" fill="#999">Loading...</text></svg>')}`;

  // Create enriched image items for display with URLs, imageIds, and visibility.
  // The blobUrlCache prevents duplicate blob URLs. Revoking happens in the cleanup
  // effect. eslint react-hooks/refs is disabled here since the cache is local and
  // not part of React's rendering inputs.
  /* eslint-disable react-hooks/refs */
  const imageItems = useMemo<ImageGridImage[]>(() => {
    const imagesToShow = uploadedImages.length > 0 ? uploadedImages : completedUploads;

    return imagesToShow.map((item: { thumbnailUrl?: string; file?: File | null; imageId?: string; visibility?: 'public' | 'private'; uploadedAt?: string | null; status?: string; filename?: string; width?: number; height?: number; fileSize?: number; progress?: number }) => {
      // Cache blob URLs by imageId+name+size so revoking happens centrally
      // in the cleanup effect. selectPreviewUrl skips the blob branch entirely
      // for non-browser-renderable formats (TIFF/RAW/HEIC-outside-Safari).
      const createBlobUrl = (file: File): string => {
        const fileKey = `${item.imageId || ''}-${file.name}-${file.size}`;
        const cached = blobUrlCache.current.get(fileKey);
        if (cached) return cached;
        const fresh = URL.createObjectURL(file);
        blobUrlCache.current.set(fileKey, fresh);
        return fresh;
      };

      let url: string;
      try {
        url = selectPreviewUrl(item, placeholderUrl, createBlobUrl);
      } catch (error) {
        logger.debug('Failed to select preview URL for image', error);
        url = placeholderUrl;
      }

      // Surface card metadata: prefer the persisted CatalogImage fields when
      // available, otherwise fall back to the raw File so newly added uploads
      // show their filename + size immediately even before dimensions land.
      const filename = item.filename ?? item.file?.name;
      const fileSize = typeof item.fileSize === 'number' ? item.fileSize : item.file?.size;

      return {
        url,
        imageId: item.imageId,
        visibility: item.visibility || 'private',
        uploadedAt: item.uploadedAt ?? null,
        status: item.status,
        filename,
        width: item.width,
        height: item.height,
        fileSize,
        progress: item.progress,
      };
    });
  }, [uploadedImages, completedUploads, placeholderUrl]);
  /* eslint-enable react-hooks/refs */

  // Update upload context when queue items complete or get thumbnailUrl
  // Note: This sync is for NEW uploads only - edit mode images are added directly via addImage()
  // Matching logic: Match by imageId only (imageId is shared between context and queue since #662 fix)
  useEffect(() => {
    uploadQueue.forEach(queueItem => {
      if (
        queueItem.status === 'completed' ||
        queueItem.status === 'processing' ||
        queueItem.status === 'processing-timeout'
      ) {
        // Match by imageId only - this is the shared identifier between context and upload queue
        const existingImage = uploadedImages.find(img =>
          queueItem.imageId && img.imageId === queueItem.imageId
        );

        if (existingImage) {
          // Sync if status changed OR if we got a new thumbnailUrl
          const statusChanged = existingImage.status !== queueItem.status;
          const thumbnailChanged = queueItem.thumbnailUrl && existingImage.thumbnailUrl !== queueItem.thumbnailUrl;

          if (statusChanged || thumbnailChanged) {
            updateImageStatus(
              existingImage.imageId,
              queueItem.status as 'pending' | 'uploading' | 'processing' | 'completed' | 'failed' | 'processing-timeout',
              queueItem.thumbnailUrl,
            );
          }
        }

        // Set catalog ID from the first completed upload
        if (queueItem.catalogId && !catalogId) {
          setCatalogId(queueItem.catalogId);
        }

        // Sync ALI catalog number from queue to context (only set once, don't overwrite with null)
        if (queueItem.aliCatalogNumber && !aliCatalogNumber) {
          setAliCatalogNumber(queueItem.aliCatalogNumber);
        }
      }
    });
  }, [uploadQueue, uploadedImages, updateImageStatus, catalogId, setCatalogId, aliCatalogNumber, setAliCatalogNumber]);

  // Track completion for upload steps
  useEffect(() => {
    // Step 0: Add Your Images
    markStepComplete('upload', 0, uploadedImages.length > 0);

    // Step 1: Choose Your Main Image
    markStepComplete('upload', 1, selectedCoverId !== null);
  }, [uploadedImages, selectedCoverId, markStepComplete]);

  // Guard against accidental navigation mid-upload (#1056).
  // Catalog numbers are assigned at initiation; abandoning an upload leaves a gap in the sequence.
  // The browser's built-in dialog is triggered by setting event.returnValue.
  useEffect(() => {
    const hasActiveUploads = uploadQueue.some(
      item => item.status === 'uploading' || item.status === 'pending'
    );
    if (!hasActiveUploads) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Modern browsers show their own generic message; setting returnValue triggers the dialog.
      event.returnValue = 'An upload is in progress. Leaving the page will cancel it.';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploadQueue]);

  const isUploading = uploadQueue.some(item => item.status === 'uploading' || item.status === 'pending');

  // Auto-clear completed uploads after all active uploads finish.
  // Uses clearCompleted() directly (not handleClearCompleted) because
  // auto-clear should NOT reset selectedCoverId - the user may have
  // already selected a cover image from the grid.
  const hasCompletedItems = uploadQueue.some(
    item => item.status === 'completed' || item.status === 'processing'
  );

  useEffect(() => {
    if (!isUploading && uploadQueue.length > 0 && hasCompletedItems) {
      const timerId = setTimeout(() => {
        clearCompleted();
      }, AUTO_CLEAR_DELAY_MS);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [isUploading, uploadQueue, hasCompletedItems, clearCompleted]);

  // Upload queue management with useCallback for performance
  const handleCancelAll = useCallback(() => {
    const itemsToCancel = uploadQueue.filter(item => item.status === 'uploading' || item.status === 'pending');
    itemsToCancel.forEach(item => cancelUpload(item.id));
  }, [uploadQueue, cancelUpload]);

  const handleRetryFailed = useCallback(() => {
    const failedItems = uploadQueue.filter(item => item.status === 'failed');
    failedItems.forEach(item => retryUpload(item.id));
  }, [uploadQueue, retryUpload]);

  const handleClearCompleted = useCallback(() => {
    clearCompleted();
    // Reset selected cover when clearing completed uploads
    setSelectedCoverId(null);
  }, [clearCompleted, setSelectedCoverId]);

  const handleRemoveItem = useCallback((fileId: string) => {
    removeItem(fileId);
  }, [removeItem]);

  // The flat-flow "Add more" tile sits at the end of the grid and should
  // open the same picker as the dropzone. ImageUploadArea hides its file
  // <input> behind a label, so we click that input directly via the
  // testid hook the dropzone already exposes. If the input is missing
  // (DOM structure changed, JSDOM in tests), log a warning so the failure
  // is debuggable instead of silently scrolling.
  const handleAddMoreClick = useCallback(() => {
    const dropzone = document.querySelector('[data-testid="image-upload-area"]');
    const input = dropzone?.querySelector('input[type="file"]') as HTMLInputElement | null;
    if (input) {
      input.click();
      return;
    }
    logger.warn('UploadTab: AddMore tile could not locate the dropzone file input; falling back to scroll.');
    dropzone?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleDeleteRequest = useCallback((imageId: string) => {
    setPendingDeleteId(imageId);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteId) return;
    const idToDelete = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await deleteImage(idToDelete);
    } catch (err) {
      logger.error('UploadTab - Failed to delete image', err);
    }
  }, [pendingDeleteId, deleteImage]);

  const handleDeleteCancel = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  // Convert upload queue items to progress format for UploadQueue component
  const uploadProgressItems = useMemo(() => {
    return uploadQueue.map(item => ({
      fileId: item.id,
      fileName: item.file.name,
      fileSize: item.file.size,
      uploadedBytes: item.uploadedBytes,
      totalChunks: item.totalChunks,
      completedChunks: item.completedChunks,
      uploadSpeed: item.uploadSpeed,
      estimatedTimeRemaining: item.estimatedTimeRemaining,
      status: item.status,
      error: item.error,
    }));
  }, [uploadQueue]);

  // Cleanup cached blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    const cache = blobUrlCache.current;
    return () => {
      // Revoke all cached blob URLs on unmount
      cache.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          logger.debug('Failed to revoke blob URL', error);
        }
      });
      cache.clear();
    };
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {visibilityError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {visibilityError}
        </Alert>
      )}
      {coverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {coverError}
        </Alert>
      )}
      {reorderError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reorderError}
        </Alert>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}
      {uploadStartError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="upload-start-error"
          onClose={() => setUploadStartError(null)}
        >
          {uploadStartError}
        </Alert>
      )}

      {/* Confirmation dialog for image deletion */}
      <Dialog open={pendingDeleteId !== null} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the image and all its processed versions. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {flat ? (
        <FlatUploadLayout
          imageUploadArea={
            <ImageUploadArea
              onFileUpload={handleFileUpload}
              onValidationError={handleValidationError}
              validationErrors={validationErrors}
              isUploading={isUploading}
              currentImageCount={imageItems.length}
              compact
              data-testid="image-upload-area"
            />
          }
          imageGrid={imageItems.length === 0 ? null : (
            <ImageGrid
              images={imageItems}
              selectable
              selectedIndex={selectedCover}
              onImageSelect={handleCoverSelect}
              primaryImageId={selectedCoverId}
              // Image-level visibility is deferred to the Review step so the
              // user makes every publish-time decision (object, field, image)
              // in one place; per-image toggles here would split that mental
              // model across two steps.
              showVisibilityToggle={false}
              showDeleteButton={!!catalogId}
              onDeleteImage={handleDeleteRequest}
              onReorder={handleReorderAndPromote}
              trailingCell={
                imageItems.length < MAX_IMAGES_PER_ITEM && !isUploading ? (
                  <AddMoreTile onClick={handleAddMoreClick} />
                ) : null
              }
              data-testid="image-grid"
            />
          )}
          imageCount={imageItems.length}
          maxImages={MAX_IMAGES_PER_ITEM}
        />
      ) : (
        <>
          <WizardAccordion
            id="upload-images"
            title={en.wizard.upload.addImages.title}
            subtitle={en.wizard.upload.addImages.subtitle}
            defaultExpanded={true}
            data-testid="wizard-accordion"
            privacyFieldPath="upload.images"
          >
            <ImageUploadArea
              onFileUpload={handleFileUpload}
              onValidationError={handleValidationError}
              validationErrors={validationErrors}
              isUploading={isUploading}
              currentImageCount={imageItems.length}
              data-testid="image-upload-area"
            />

            {/* Upload Queue Display */}
            {uploadQueue.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <UploadQueue
                  uploadQueue={uploadProgressItems}
                  collapsed={queueCollapsed}
                  onToggleCollapse={() => setQueueCollapsed(!queueCollapsed)}
                  onCancelAll={handleCancelAll}
                  onRetryFailed={handleRetryFailed}
                  onClearCompleted={handleClearCompleted}
                  onCancel={cancelUpload}
                  onRetry={retryUpload}
                  onRemove={handleRemoveItem}
                  data-testid="upload-queue"
                />
              </Box>
            )}
          </WizardAccordion>

          {aliCatalogNumber && (
            <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {en.imageThumbnail.catalogNumberPrefix}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {aliCatalogNumber}
              </Typography>
            </Box>
          )}

          <WizardAccordion
            id="upload-cover"
            title={en.wizard.upload.selectCover.title}
            subtitle={en.wizard.upload.selectCover.subtitle}
            defaultExpanded={true}
            data-testid="wizard-accordion"
            privacyFieldPath="upload.coverImage"
          >
            {imageItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {en.wizard.upload.selectCover.noImages}
              </Typography>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  The <strong>primary image</strong> appears on the object card and public pages.
                  {' '}Click the star on a non-primary image to promote it.
                  {' '}<Box component="span" sx={{ color: 'text.secondary' }}>
                    ({imageItems.length} of {MAX_IMAGES_PER_ITEM} images)
                  </Box>
                </Alert>
                <ImageGrid
                  images={imageItems}
                  selectable
                  selectedIndex={selectedCover}
                  onImageSelect={handleCoverSelect}
                  showVisibilityToggle={!!catalogId}
                  onToggleVisibility={toggleVisibility}
                  showDeleteButton={!!catalogId}
                  onDeleteImage={handleDeleteRequest}
                  onReorder={reorderAndPersist}
                  data-testid="image-grid"
                />
                {selectedCover !== null && (
                  <Typography variant="body2" color="primary" sx={{ mt: 2, textAlign: 'center' }}>
                    {en.wizard.upload.selectCover.selected}
                  </Typography>
                )}
              </>
            )}
          </WizardAccordion>
        </>
      )}
    </Box>
  );
};

interface FlatUploadLayoutProps {
  imageUploadArea: React.ReactNode;
  imageGrid: React.ReactNode;
  imageCount: number;
  maxImages: number;
}

/**
 * Flat-flow layout for the redesigned edit-object Upload Images step.
 * Drops the two `WizardAccordion` wrappers AND the standalone UploadQueue
 * in favor of the mock's linear sequence: dropzone → info banner →
 * section header → unified grid. In-flight uploads render inside the
 * grid as cards with a progress overlay (see ImageGrid's UPLOADING_STATUSES
 * branch), so a separate queue list would just duplicate that signal.
 * Lives inline with UploadTab so both layouts share a single source of
 * upload-state truth.
 */
const FlatUploadLayout: React.FC<FlatUploadLayoutProps> = ({
  imageUploadArea,
  imageGrid,
  imageCount,
  maxImages,
}) => (
  <Box>
    {imageUploadArea}

    <Box
      data-testid="upload-flat-info-banner"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        mt: 2,
        px: 2,
        py: 1.5,
        bgcolor: '#fafafa',
        border: `1px solid ${EDIT_OBJECT_TOKENS.color.lineSoft}`,
        borderRadius: `${EDIT_OBJECT_TOKENS.radius.sm}px`,
        fontSize: 13,
        color: EDIT_OBJECT_TOKENS.color.ink700,
      }}
    >
      <InfoIcon sx={{ fontSize: 18, color: EDIT_OBJECT_TOKENS.color.slate500, flexShrink: 0, mt: 0.25 }} />
      <Typography variant="body2" sx={{ color: 'inherit' }}>
        The <strong>primary image</strong> is whichever sits in the leftmost slot — it
        appears on the object card and on public pages. Drag any image into the
        leftmost position to make it primary.
      </Typography>
    </Box>

    {imageGrid ? (
      <>
        <SectionHead
          title="Uploaded Images"
          count={`${imageCount} of ${maxImages} images`}
        />
        {imageGrid}
      </>
    ) : (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', py: 4, mt: 2 }}
      >
        Upload images to get started.
      </Typography>
    )}
  </Box>
);
